import classNames from "classnames";
import { Typography } from "PFComponents/typography";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MIN_DAY_WIDTH } from "../../calendar";
import { getDaysDifference } from "../../calendar.utils";
import css from "../../days/days.module.scss";

type DayItemProps = {
  date: string;
  startDate: string;
  dayWidth: number;
  daysHeight: number;
};

export const DayItem = ({ date, startDate, dayWidth, daysHeight }: DayItemProps): JSX.Element => {
  const { formatISODate, utc } = useDateFormatter();
  const { t } = useTranslation("translation");
  const { formattedDate, dayOffset, isToday, isFirstDayOfWeek, currentDayDate } = useMemo(() => {
    const currentDayDate = utc(date);
    const formattedDate = formatISODate(date);
    const isToday = formattedDate === formatISODate();
    const isFirstDayOfWeek = currentDayDate.weekday() === 0;
    const dayOffset = Math.floor(getDaysDifference(new Date(startDate), new Date(date)));
    return { formattedDate, dayOffset, isToday, isFirstDayOfWeek, currentDayDate };
  }, [date, startDate, formatISODate, utc]);

  const hideDates = dayWidth < MIN_DAY_WIDTH;
  return (
    <div
      key={formattedDate}
      className={css.day}
      title={formattedDate}
      style={{ width: dayWidth, left: dayWidth * dayOffset }}
    >
      <div
        className={classNames(css.dayDate, {
          [css.today]: isToday,
          [css.firstDayOfWeek]: isFirstDayOfWeek,
          [css.hideDate]: hideDates
        })}
        style={{
          height: daysHeight
        }}
      >
        {isToday ? (
          <Typography variant="bodyRegular" tag="span">
            {t("today")}
          </Typography>
        ) : (
          <>
            <Typography variant="bodyRegular" tag="span" className={css.dayOfWeek}>
              {currentDayDate.format("dd")}
            </Typography>
            <Typography variant="bodyRegular" tag="span">
              {currentDayDate.format("D")}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
};
